import { getText, getValue } from 'lib/value'

const answerValueToText = value => {
    if (!value) return null

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            if (Array.isArray(parsed?.ops)) {
                return getText(parsed, { excludeActionTokens: true })
            }
        } catch {}
    }

    return getText(value, { excludeActionTokens: true })
}

const AI_IMAGE_OUTPUT_EXTENSION_PATTERN = /\.(gif|jpe?g|png|webp|avif|heic|heif)(?:[?#]|$)/i

const mediaDockAiOutputIsImage = output => {
    const kind = String(output?.type || output?.kind || '').toLowerCase()
    const contentType = String(
        output?.mime || output?.content_type || output?.contentType || '',
    ).toLowerCase()

    if (kind === 'image' || contentType.startsWith('image/')) return true
    if (kind || contentType) return false

    const urls = output?.urls || {}
    const candidateUrl =
        output?.url ||
        output?.preview_url ||
        output?.previewUrl ||
        urls.original ||
        urls.large ||
        urls.medium ||
        urls.small ||
        ''
    return AI_IMAGE_OUTPUT_EXTENSION_PATTERN.test(candidateUrl)
}

const mediaDockAiOutputArtwork = output => {
    const urls = output?.urls || {}
    const previewUrls = output?.preview_urls || output?.previewUrls || {}

    return (
        output?.preview_url ||
        output?.previewUrl ||
        output?.thumbnail_url ||
        output?.thumbnailUrl ||
        previewUrls['1x1'] ||
        previewUrls.medium ||
        previewUrls.small ||
        urls.list ||
        urls.medium ||
        urls.small ||
        urls.large ||
        urls.original ||
        output?.url ||
        null
    )
}

const latestSuccessfulAiImageArtwork = answer => {
    const runs = Array.isArray(answer?.ai_runs) ? answer.ai_runs : []

    for (const run of [...runs].reverse()) {
        if (String(run?.status || '').toLowerCase() !== 'succeeded') continue

        const outputs = Array.isArray(run?.output_urls) ? run.output_urls : []
        for (const output of [...outputs].reverse()) {
            if (!mediaDockAiOutputIsImage(output)) continue

            const artwork = mediaDockAiOutputArtwork(output)
            if (artwork) return artwork
        }
    }

    return null
}

export function deriveMediaDockAnswerTitle(answer) {
    // A song posted through New Music carries its own title.
    const musicTitle = answer?.music_title?.trim()
    if (musicTitle) return musicTitle

    const text = answerValueToText(getValue(answer))

    const normalizedText = text?.replace(/\s+/g, ' ').trim()
    return normalizedText ? normalizedText.slice(0, 500) : null
}

export function deriveMediaDockAnswerArtwork(answer) {
    const currentImage = answer?.images_v2?.[0]
    const currentImageUrls = currentImage?.urls || {}
    const legacyImages = Array.isArray(answer?.image_urls) ? answer.image_urls : []
    const latestLegacyImage = legacyImages[legacyImages.length - 1]
    const legacyPreviewUrls = latestLegacyImage?.preview_urls || {}
    const imageFile = answer?.file_urls?.find(file =>
        String(file?.content_type || file?.contentType || file?.type || '').startsWith('image/'),
    )
    const imageFilePreviewUrls = imageFile?.preview_urls || {}
    const aiImageArtwork = latestSuccessfulAiImageArtwork(answer)

    return (
        answer?.local_answer_image_url ||
        answer?.local_image_urls?.find(Boolean) ||
        currentImage?.preview_url ||
        currentImageUrls.list ||
        currentImageUrls.medium ||
        currentImageUrls.small ||
        currentImageUrls.large ||
        currentImageUrls.original ||
        latestLegacyImage?.preview_url ||
        legacyPreviewUrls.list ||
        legacyPreviewUrls.medium ||
        legacyPreviewUrls.small ||
        legacyPreviewUrls.large ||
        legacyPreviewUrls.original ||
        latestLegacyImage?.image_url ||
        answer?.answer_image_url ||
        imageFile?.preview_url ||
        imageFilePreviewUrls.list ||
        imageFilePreviewUrls.medium ||
        imageFilePreviewUrls.small ||
        imageFilePreviewUrls.large ||
        imageFilePreviewUrls.original ||
        imageFile?.url ||
        aiImageArtwork ||
        null
    )
}

export function getMediaDockTrackDisplayArtwork(track, options = {}) {
    const { answerArtwork, getAnswerById } = options
    if (track?.posterSrc) return track.posterSrc
    if (answerArtwork) return answerArtwork

    const answerId = track?.answerId
    return answerId && typeof getAnswerById === 'function'
        ? deriveMediaDockAnswerArtwork(getAnswerById(String(answerId)))
        : null
}

export function getMediaDockTrackDisplayTitle(track, options = {}) {
    const { getAnswerById } = options
    const answerId = track?.answerId
    const answerTitle =
        answerId && typeof getAnswerById === 'function'
            ? deriveMediaDockAnswerTitle(getAnswerById(String(answerId)))
            : null

    return answerTitle || track?.title || null
}
